import { useTranslation } from "react-i18next";
import { useEstimate } from "../store/EstimateContext";
import { LOCATION_PRESETS, DAY_LABELS } from "../lib/s1000dData";
import { fmtFte, fmtHours, fmtPercent } from "../lib/calculations";
import { Calendar, Users } from "lucide-react";

import HelpIconButton from "./HelpIconButton";
export default function ResourcingTab() {
  const { t } = useTranslation();
  const {
    estimate, resourcing, totals,
    updateResourcePlan, applyLocationPreset, toggleWeekendDay,
  } = useEstimate();

  const plan = estimate.resourcePlan;
  const utilisation = resourcing.utilisation;
  const wkSet = new Set(plan.weekendDays || []);
  const datesValid = resourcing.valid;
  const dateError = (plan.startDate && plan.endDate && !datesValid)
    ? "End date must be the same as or after start date."
    : "";

  return (
    <section className="space-y-6" data-testid="resourcing-tab">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
          {t("resourcing.section")}
        </p>
        <h2 className="font-heading text-2xl tracking-tight font-bold text-black flex items-center gap-2">
          {t("resourcing.title")}
          <HelpIconButton anchor="ch3" testId="help-icon-resourcing" />
        </h2>
        <p className="text-sm text-gray-600 mt-1 max-w-2xl">
          {t("resourcing.description")}
        </p>
      </div>

      {/* INPUTS */}
      <div className="bg-white border border-gray-300 p-5 space-y-5" data-testid="resourcing-inputs">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Calendar size={16} strokeWidth={2} className="text-[#002FA7]" />
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              {t("resourcingUi.inputs")}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 block">
              {t("resourcingUi.location")}
            </label>
            <select
              data-testid="rp-location-select"
              value={plan.locationPreset}
              onChange={(e) => applyLocationPreset(e.target.value)}
              className="border border-gray-300 rounded-none px-2 py-1.5 mt-1 font-mono text-sm bg-white w-full"
            >
              {LOCATION_PRESETS.map((p) => (
                <option key={p.key} value={p.key}>{p.key === "CUSTOM" ? t("locations.CUSTOM") : p.label}</option>
              ))}
            </select>
            <p className="text-[10px] text-gray-500 mt-1 font-mono">
              {t("resourcingUi.locationHelp")}
            </p>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 block">
              {t("resourcingUi.startDate")}
            </label>
            <input
              type="date"
              value={plan.startDate || ""}
              onChange={(e) => updateResourcePlan("startDate", e.target.value)}
              data-testid="rp-start-date"
              className="border border-gray-300 rounded-none px-2 py-1.5 mt-1 font-mono text-sm w-full"
            />
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 block">
              {t("resourcingUi.endDate")}
            </label>
            <input
              type="date"
              value={plan.endDate || ""}
              onChange={(e) => updateResourcePlan("endDate", e.target.value)}
              data-testid="rp-end-date"
              className="border border-gray-300 rounded-none px-2 py-1.5 mt-1 font-mono text-sm w-full"
            />
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 block">
              {t("resourcingUi.publicHolidays")}
            </label>
            <input
              type="number"
              min="0"
              step="1"
              value={plan.publicHolidays}
              onChange={(e) => updateResourcePlan("publicHolidays", Number(e.target.value) || 0)}
              data-testid="rp-public-holidays"
              className="border border-gray-300 rounded-none px-2 py-1.5 mt-1 font-mono text-sm w-full text-right"
            />
            <p className="text-[10px] text-gray-500 mt-1 font-mono">
              {t("resourcingUi.publicHolidaysHelp")}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="border border-gray-300 p-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              {t("resourcingUi.weekendDays")}
            </p>
            <p className="text-[10px] text-gray-500 mt-1 font-mono">
              {t("resourcingUi.weekendDaysHelp")}
            </p>
            <div className="grid grid-cols-7 gap-1 mt-2" data-testid="rp-weekend-grid">
              {DAY_LABELS.map((d, idx) => {
                const dayKey = ["sun","mon","tue","wed","thu","fri","sat"][idx];
                const active = wkSet.has(idx);
                return (
                  <button
                    key={d}
                    onClick={() => toggleWeekendDay(idx)}
                    data-testid={`rp-day-${idx}`}
                    className={
                      "py-2 text-[10px] font-bold uppercase tracking-wider border transition-colors " +
                      (active
                        ? "bg-black text-white border-black"
                        : "bg-white text-gray-500 border-gray-300 hover:bg-gray-100")
                    }
                  >
                    {t(`days.${dayKey}`)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="border border-gray-300 p-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              {t("resourcingUi.hoursPerDay")}
            </p>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 block">
                  {t("resourcingUi.standard")}
                </label>
                <input
                  type="number"
                  min="1"
                  max="24"
                  step="0.5"
                  value={plan.standardHoursPerDay}
                  onChange={(e) => updateResourcePlan("standardHoursPerDay", Number(e.target.value) || 0)}
                  data-testid="rp-standard-hours"
                  className="border border-gray-300 rounded-none px-2 py-1.5 mt-1 font-mono text-sm w-full text-right"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 block">
                  {t("resourcingUi.effective")}
                </label>
                <input
                  type="number"
                  min="0"
                  max="24"
                  step="0.5"
                  value={plan.effectiveHoursPerDay}
                  onChange={(e) => updateResourcePlan("effectiveHoursPerDay", Number(e.target.value) || 0)}
                  data-testid="rp-effective-hours"
                  className="border border-gray-300 rounded-none px-2 py-1.5 mt-1 font-mono text-sm w-full text-right"
                />
              </div>
            </div>
            <p className="text-[10px] text-gray-500 mt-2 font-mono" data-testid="rp-utilisation">
              {t("resourcingUi.utilisation", { pct: fmtPercent(utilisation * 100) })}
              {plan.standardHoursPerDay > 0 && plan.effectiveHoursPerDay > plan.standardHoursPerDay
                ? " · " + t("resourcingUi.effExceedsStd")
                : ""}
            </p>
          </div>

          <div className="border border-gray-300 p-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              {t("resourcingUi.projectWindow")}
            </p>
            {dateError ? (
              <p className="font-mono text-sm text-[#FF2400] mt-2">{dateError}</p>
            ) : !datesValid ? (
              <p className="font-mono text-sm text-gray-400 mt-2">{t("resourcingUi.pickDates")}</p>
            ) : (
              <ul className="font-mono text-sm text-gray-800 mt-2 space-y-1">
                <li><span className="text-gray-500 mr-2">{t("resourcingUi.calendarDays")}</span>{resourcing.calendarDays}</li>
                <li><span className="text-gray-500 mr-2">{t("resourcingUi.weekends")}</span>{resourcing.weekendsCount}</li>
                <li><span className="text-gray-500 mr-2">{t("resourcingUi.holidays")}</span>{Number(plan.publicHolidays) || 0}</li>
                <li className="border-t border-gray-200 pt-1 mt-1">
                  <span className="text-gray-500 mr-2">{t("resourcingUi.workingDays")}</span>
                  <span className="font-bold text-black" data-testid="rp-working-days">
                    {resourcing.workingDays}
                  </span>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* OUTPUTS — RESOURCES */}
      <div>
        <div className="flex items-center gap-2">
          <Users size={16} strokeWidth={2} className="text-[#002FA7]" />
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            {t("resourcingUi.output")}
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
          <div className="bg-white border border-gray-300 p-4" data-testid="rp-stat-project-hours">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              {t("resourcingUi.totalProjectHours")}
            </p>
            <p className="font-mono text-2xl font-bold text-black leading-none mt-2">
              {fmtHours(totals.totalHours)}
            </p>
            <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-2">
              {t("resourcingUi.totalProjectHoursHelp")}
            </p>
          </div>
          <div className="bg-white border border-gray-300 p-4" data-testid="rp-stat-available-std">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              {t("resourcingUi.availableStandard")}
            </p>
            <p className="font-mono text-2xl font-bold text-black leading-none mt-2">
              {fmtHours(resourcing.availableStandardHours)}
            </p>
            <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-2">
              {t("resourcingUi.daysHoursFormula", { days: resourcing.workingDays, hrs: plan.standardHoursPerDay })}
            </p>
          </div>
          <div className="bg-white border border-gray-300 p-4" data-testid="rp-stat-available-eff">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              {t("resourcingUi.availableEffective")}
            </p>
            <p className="font-mono text-2xl font-bold text-black leading-none mt-2">
              {fmtHours(resourcing.availableEffectiveHours)}
            </p>
            <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-2">
              {t("resourcingUi.daysHoursFormula", { days: resourcing.workingDays, hrs: plan.effectiveHoursPerDay })}
            </p>
          </div>
          <div className="bg-white border border-gray-300 p-4" data-testid="rp-stat-fte">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              {t("resourcingUi.requiredFte")}
            </p>
            <p className="font-mono text-2xl font-bold text-[#002FA7] leading-none mt-2" data-testid="rp-fte-effective">
              {fmtFte(resourcing.fteEffective)}
            </p>
            <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-2">
              {t("resourcingUi.fteAtStandard", { n: fmtFte(resourcing.fteStandard) })}
            </p>
          </div>
        </div>
      </div>

      {/* PER-ROLE FTE TABLE */}
      <div className="bg-white border border-gray-300" data-testid="rp-role-table">
        <div className="border-b border-gray-300 p-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              {t("resourcingUi.perRoleDemand")}
            </p>
            <h3 className="font-heading text-lg tracking-tight font-bold text-black">
              {t("resourcingUi.headcountByRole")}
            </h3>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              {t("resourcingUi.recommendedHeadcount")}
            </p>
            <p className="font-mono text-2xl font-bold text-black" data-testid="rp-total-headcount">
              {resourcing.totalHeadcount}
            </p>
            <p className="text-[10px] uppercase tracking-wider text-gray-500">
              {t("resourcingUi.headcountSum")}
            </p>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-100 text-black font-bold uppercase tracking-wider text-[10px] border-b border-gray-300">
              <th className="py-2 px-3 text-left">{t("tableHeaders.role")}</th>
              <th className="py-2 px-3 text-right">{t("tableHeaders.effortPercent")}</th>
              <th className="py-2 px-3 text-right">{t("tableHeaders.hours")}</th>
              <th className="py-2 px-3 text-right">{t("tableHeaders.fteEffective")}</th>
              <th className="py-2 px-3 text-right">{t("tableHeaders.fteStandard")}</th>
              <th className="py-2 px-3 text-right">{t("tableHeaders.headcount")}</th>
            </tr>
          </thead>
          <tbody>
            {resourcing.roleFteBreakdown.map((r, idx) => (
              <tr key={idx} className="border-b border-gray-200 hover:bg-[#F9FAFB]" data-testid={`rp-role-row-${idx}`}>
                <td className="py-2 px-3 font-semibold">{r.name}</td>
                <td className="py-2 px-3 font-mono text-right">{fmtPercent(r.effortPercent)}</td>
                <td className="py-2 px-3 font-mono text-right">{fmtHours(r.roleHours)}</td>
                <td className="py-2 px-3 font-mono text-right text-black font-bold" data-testid={`rp-role-${idx}-fte-effective`}>
                  {fmtFte(r.fteEffective)}
                </td>
                <td className="py-2 px-3 font-mono text-right text-gray-700">{fmtFte(r.fteStandard)}</td>
                <td className="py-2 px-3 font-mono text-right text-[#002FA7] font-bold" data-testid={`rp-role-${idx}-headcount`}>
                  {r.headcount}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-black text-white font-bold uppercase tracking-wider text-[10px]">
              <td className="py-2 px-3" colSpan={2}>{t("tableHeaders.projectTotal")}</td>
              <td className="py-2 px-3 font-mono text-right normal-case">{fmtHours(totals.totalHours)}</td>
              <td className="py-2 px-3 font-mono text-right normal-case">{fmtFte(resourcing.fteEffective)}</td>
              <td className="py-2 px-3 font-mono text-right normal-case">{fmtFte(resourcing.fteStandard)}</td>
              <td className="py-2 px-3 font-mono text-right normal-case">{resourcing.totalHeadcount}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}
